/* eslint-disable react/prop-types */
import React, { createContext, useContext } from 'react'
import * as Select from '@radix-ui/react-select'
import * as rawStyles from './styles.module.scss'
import { SelectTriggerProps, SelectItemProps, SelectProps } from './types'
import CheckIcon from './assets/CheckIcon'
import ChevronDownIcon from './assets/ChevronDownIcon'
import ChevronUpIcon from './assets/ChevronUpIcon'

const styles: any = rawStyles

const TriggerContext = createContext<{ error?: boolean; valid?: boolean }>({
    error: undefined,
    valid: undefined,
})

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof Select.Separator>,
    React.ComponentPropsWithoutRef<typeof Select.Separator>
>(({ className, ...props }, forwardRef) => {
    return (
        <Select.Separator
            ref={forwardRef}
            className={`${styles.select_separator} ${className}`}
            {...props}
        />
    )
})
SelectSeparator.displayName = 'Select.Separator'

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof Select.Label>,
    React.ComponentPropsWithoutRef<typeof Select.Label>
>(({ children, className, ...props }, forwardRef) => {
    return (
        <Select.Label
            ref={forwardRef}
            className={`${styles.select_item_label} ${className}`}
            {...props}>
            {children}
        </Select.Label>
    )
})
SelectLabel.displayName = 'Select.Label'

const SelectItem = React.forwardRef<React.ElementRef<typeof Select.Item>, SelectItemProps>(
    ({ children, className, selectedIcon, ...props }, forwardedRef) => {
        return (
            <Select.Item
                className={`${styles.select_item} ${styles.select_default_item} ${className}`}
                ref={forwardedRef}
                {...props}>
                <Select.ItemText>{children}</Select.ItemText>
                <Select.ItemIndicator className={styles.select_item_indicator}>
                    {selectedIcon ? selectedIcon : <CheckIcon />}
                </Select.ItemIndicator>
            </Select.Item>
        )
    },
)
SelectItem.displayName = 'Select.Item'

const SelectTrigger = React.forwardRef<React.ElementRef<typeof Select.Trigger>, SelectTriggerProps>(
    ({ className, placeholder, icon, ...props }, forwardedRef) => {
        const { error, valid } = useContext(TriggerContext)
        return (
            <Select.Trigger
                className={`${styles.select_trigger} ${styles.select_default_trigger} ${className}`}
                data-error={error?.toString()}
                data-valid={valid?.toString()}
                ref={forwardedRef}
                {...props}>
                <Select.Value placeholder={placeholder} />
                <Select.Icon className={styles.icon}>
                    {icon ? icon : <ChevronDownIcon />}
                </Select.Icon>
            </Select.Trigger>
        )
    },
)
SelectTrigger.displayName = 'Select.Trigger'

const SelectComponent: React.FC<SelectProps> & {
    Group: typeof Select.Group
    Trigger: typeof SelectTrigger
    Label: typeof SelectLabel
    Item: typeof SelectItem
    Separator: typeof SelectSeparator
} = ({
    children,
    button,
    contentClassname,
    contentWrapperClassname,
    chevronDownIcon,
    chevronUpIcon,
    error,
    valid,
    ...props
}) => {
    return (
        <TriggerContext.Provider value={{ error, valid }}>
            <Select.Root {...props}>
                <div className={styles.select_button}>{button}</div>
                <Select.Portal>
                    <Select.Content
                        className={`${styles.select_content} ${styles.select_default_content} ${contentClassname}`}>
                        <Select.ScrollUpButton className={styles.select_scroll_button}>
                            {chevronUpIcon ? chevronUpIcon : <ChevronUpIcon />}
                        </Select.ScrollUpButton>
                        <Select.Viewport
                            className={`${styles.select_content_wrapper} ${contentWrapperClassname}`}>
                            {children}
                        </Select.Viewport>
                        <Select.ScrollDownButton className={styles.select_scroll_button}>
                            {chevronDownIcon ? chevronDownIcon : <ChevronDownIcon />}
                        </Select.ScrollDownButton>
                    </Select.Content>
                </Select.Portal>
            </Select.Root>
        </TriggerContext.Provider>
    )
}
SelectComponent.displayName = 'Select'

SelectComponent.Group = Select.Group
SelectComponent.Trigger = SelectTrigger
SelectComponent.Label = SelectLabel
SelectComponent.Item = SelectItem
SelectComponent.Separator = SelectSeparator

export default SelectComponent
